import React, { useState } from 'react';
import { Box, VStack, Text, Heading, HStack, Icon } from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useRecoveryStartMutation } from '../../api';
import Button from '../../components/base/Button';

export interface RecoveryRequestLocationState {
    email: string;
    companyName?: string;
    login?: string;
}

const RecoveryRequestPage: React.FC = () => {
    const { t } = useTranslation({ setLangFromURL: true });
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as RecoveryRequestLocationState | null;
    const email = state?.email?.trim() || '';
    const companyName = state?.companyName;
    const loginFromState = state?.login?.trim();
    const login = loginFromState || (email ? email.split('@')[0] : '');

    const [recoveryStart, { isLoading }] = useRecoveryStartMutation();
    const [success, setSuccess] = useState(false);
    const [invalidEmailError, setInvalidEmailError] = useState(false);

    if (!email) {
        navigate('/login', { replace: true });
        return null;
    }

    const handleRecover = async () => {
        if (!login) return;
        setInvalidEmailError(false);
        try {
            await recoveryStart({ login }).unwrap();
            setSuccess(true);
        } catch (err: unknown) {
            const status = (err as { status?: number })?.status;
            if (status === 400) {
                setInvalidEmailError(true);
            }
        }
    };

    const descriptionText = companyName
        ? t.pages.recovery.descriptionWithCompany
              .replace('{email}', email)
              .replace('{company_name}', companyName)
        : t.pages.recovery.description.replace('{email}', email);

    return (
        <Box minH="100vh" bg="gray.100" display="flex" alignItems="center" justifyContent="center" p={4}>
            <Box w="full" maxW="md" bg="white" p={8}>
                <VStack gap={6} align="stretch">
                    <HStack
                        cursor="pointer"
                        onClick={() => navigate('/login')}
                        color="gray.600"
                        _hover={{ color: 'gray.800' }}
                    >
                        <Icon as={FiArrowLeft} boxSize={5} />
                        <Text fontSize="md">{t.login.title}</Text>
                    </HStack>

                    <Heading size="lg" color="gray.800">
                        {t.pages.recovery.title}
                    </Heading>

                    {success ? (
                        <VStack gap={4} align="stretch">
                            <Text fontSize="md" color="gray.700">
                                {t.pages.recovery.successMessage}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                                {t.pages.recovery.successHint}
                            </Text>
                        </VStack>
                    ) : (
                        <>
                            <Text fontSize="md" color="gray.700">
                                {descriptionText}
                            </Text>
                            {invalidEmailError && (
                                <Text fontSize="sm" color="red.600">
                                    {t.pages.recovery.invalidEmail}
                                </Text>
                            )}
                            <Button
                                onClick={invalidEmailError ? () => navigate('/login') : handleRecover}
                                w="full"
                                h="56px"
                                size="lg"
                                isLoading={isLoading}
                                disabled={!login && !invalidEmailError}
                                buttonType={invalidEmailError ? 'secondary' : 'success'}
                            >
                                {invalidEmailError ? t.login.form.backToLogin : t.pages.recovery.button}
                            </Button>
                        </>
                    )}
                </VStack>
            </Box>
        </Box>
    );
};

export default RecoveryRequestPage;
